import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, Card, CardContent, Button } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Breadcrumbs from "../../common/Breadcrumbs";
import FilePreviewDialog from "../../common/FilePreviewDialog";
import { fetchProfileData } from "../../api/profileAPI";
import { renderInfo, renderDocuments } from "./ProfileRenderHelpers";
import useIsMobile from "../../common/mobileVersion/useIsMobile";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { getUserEmailFromLocalStorage } from "../../utils/localStorageUtils";
import {
  downloadFileFromS3,
  uploadSingleFileWithPresignedUrl,
} from "../../utils/files";
import { getPresignedUrls } from "../../api/customerAPI";
import bucketMapping from "../../constants/S3BuketMapping";
import { uploadSignedCustomerAgreement } from "../../api/agreement";
import { fetchCustomer } from "../../service/cartService";
import Toast from "../../common/Toast";
import {
  downloadAgreementButtonStyle,
  uploadSignedAgreementButtonStyle,
} from "./CustomerProfile.styles";

const CustomerProfile = () => {
  const [profileData, setProfileData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [previewFileUrl, setPreviewFileUrl] = useState("");
  const [urlFileType, setUrlFileType] = useState("");
  const [fileError, setFileError] = useState("");
  const [error, setError] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const isMobile = useIsMobile();
  const signedAgreementInputRef = useRef<HTMLInputElement>(null);

  const getProfileData = async (forceTrigger: boolean) => {
    try {
      const data = await fetchProfileData(forceTrigger);
      setProfileData(
        data || {
          companyDetails: {},
          licenseDetails: {},
          bankDetails: {},
        }
      );
    } catch (error) {
      console.error("Error fetching profile data:", error);
      setProfileData({
        companyDetails: {},
        licenseDetails: {},
        bankDetails: {},
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProfileData(true);
  }, []);

  const hasCustomerAgreementGenerated = useMemo(() => {
    return Boolean(profileData?.customerAgreements?.unsigned?.documentId);
  }, [profileData]);

  const handleUploadSignedAgreementClick = () => {
    if (signedAgreementInputRef.current) {
      signedAgreementInputRef.current.click();
    }
  };

  const handleSignedAgreementUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    const allowedTypes = ["application/pdf"];

    if (!file || !allowedTypes.includes(file.type)) {
      setFileError("Only PDF files are allowed.");
      return;
    }

    try {
      const presignedUrls = await getPresignedUrls(
        [
          {
            documentType: "signed_agreement",
            fileName: file.name,
            fileType: file.type,
          },
        ],
        bucketMapping.customer
      );

      const presignedUrl = presignedUrls?.[0]?.presignedUrl;

      if (!presignedUrl) {
        setFileError("Failed to retrieve s3 url");
        throw new Error("Failed to retrieve s3 url");
      }

      const uploadSuccess = await uploadSingleFileWithPresignedUrl(
        presignedUrl,
        file,
        file.type
      );

      if (uploadSuccess) {
        const user = await fetchCustomer();

        await uploadSignedCustomerAgreement({
          customerId: user.id,
          payload: {
            documentId: "signed_agreement",
            fileName: file.name,
            key: presignedUrls?.[0]?.key,
          },
        });

        setShowToast(true);
        setToastMessage("Signed agreement uploaded successfully");
        getProfileData(true);
      } else {
        setFileError("Failed to upload file to s3");
        throw new Error("Failed to upload file to s3");
      }
    } catch (error) {
      setError(true);
      setShowToast(true);
      setToastMessage("Failed to upload signed customer agreement");
    }
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  const { companyDetails, licenseDetails, bankDetails } = profileData;

  const breadcrumbPaths = ["Profile", "My Details"];
  const breadcrumbLinks = [
    "/maltora-customer/customer-profile",
    "/maltora-customer/customer-profile",
  ];

  const handlePreview = async (link: string) => {
    try {
      const response = await fetch(link);
      const contentType = response.headers.get("Content-Type") || "unknown";
      setUrlFileType(contentType);
    } catch (error) {
      console.error("Error fetching content type:", error);
    }

    setPreviewFileUrl(link);
    setDownloadUrl(link);
    setOpenDialog(true);
  };

  const handleDownloadAgreement = async () => {
    if (hasCustomerAgreementGenerated) {
      try {
        const email = getUserEmailFromLocalStorage();
        const documentId =
          profileData?.customerAgreements?.unsigned?.documentId;

        if (!documentId) {
          throw new Error("Document ID is missing");
        }

        await downloadFileFromS3(documentId, `customer-agreement-${email}.pdf`);
      } catch (error) {
        console.error("Failed to download customer agreement:", error);
        setError(true);
        setShowToast(true);
        setToastMessage("Failed to download customer agreement");
      }
    }
  };

  return (
    <>
      <Breadcrumbs paths={breadcrumbPaths} links={breadcrumbLinks} />
      <Box>
        <Card sx={{ padding: isMobile ? "11px" : "36px 64px", boxShadow: 2 }}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                paddingBottom: isMobile ? "16px" : "60px",
                fontSize: isMobile ? "14px" : "inherit",
                fontFamily: "Montserrat",
              }}
            >
              Customer Details
            </Typography>

            {/* Part 1: Company Information */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                paddingBottom: isMobile ? "16px" : "30px",
                fontSize: isMobile ? "12px" : "inherit",
                fontFamily: "Montserrat",
              }}
            >
              PART 1: Company Information
            </Typography>
            <Grid container rowSpacing={2} columnSpacing={isMobile ? 2 : 4}>
              {renderInfo("Outlet Name*", companyDetails.outletName)}
              {renderInfo("Company GST Number*", companyDetails.gstNumber)}
              {renderInfo("Company PAN Number*", companyDetails.panNumber)}
              {renderInfo(
                "Company Authorized Signatory (Name)",
                companyDetails.authorizedSignatory
              )}
              {renderInfo("VAT Number (if applicable)", companyDetails.vatNo)}
              {renderInfo("GST State Code", companyDetails.gstStateCode)}
              {renderInfo("Pincode*", companyDetails.pincode)}
              {renderInfo(
                "Email ID*",
                companyDetails.email,
                false,
                false,
                true
              )}
              {renderInfo("Phone Number", companyDetails.phoneNumber)}
              {renderInfo(
                "Registered Address (as per GST)*",
                companyDetails.registeredAddress,
                true,
                true
              )}
            </Grid>

            <Box mt={3}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  paddingBottom: "8px",
                  fontFamily: "Montserrat",
                }}
              >
                Uploaded Documents
              </Typography>
              {renderDocuments(companyDetails.documents || [], handlePreview)}
            </Box>

            {/* Part 2: License Information */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                paddingTop: "60px",
                paddingBottom: isMobile ? "16px" : "30px",
                fontSize: isMobile ? "12px" : "inherit",
                fontFamily: "Montserrat",
              }}
            >
              PART 2: License Information
            </Typography>
            <Grid container rowSpacing={2} columnSpacing={isMobile ? 2 : 4}>
              {renderInfo("License Name*", licenseDetails.licenseName)}
              {renderInfo("License Type*", licenseDetails.licenseType)}
              {renderInfo(
                "Customer Excise Registration Number*",
                licenseDetails.customerExciseRegistrationNumber
              )}
              {renderInfo(
                "License PAN Number*",
                licenseDetails.licensePanNumber
              )}
              {renderInfo(
                "License GST Number*",
                licenseDetails.licenseGstNumber
              )}
              {renderInfo("GST State Code*", licenseDetails.gstStateCode)}
              {renderInfo(
                "Registered Address (as per License)*",
                licenseDetails.licenseAddress,
                true,
                true
              )}
            </Grid>

            <Box mt={3}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  paddingBottom: "8px",
                  fontFamily: "Montserrat",
                }}
              >
                Uploaded Documents
              </Typography>
              {renderDocuments(licenseDetails.documents || [], handlePreview)}
            </Box>

            {/* Part 3: Bank Information */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                paddingTop: "60px",
                paddingBottom: isMobile ? "16px" : "30px",
                fontSize: isMobile ? "12px" : "inherit",
                fontFamily: "Montserrat",
              }}
            >
              PART 3: Bank Information
            </Typography>
            <Grid container rowSpacing={2} columnSpacing={isMobile ? 2 : 4}>
              {renderInfo("Bank Name", bankDetails.bankName)}
              {renderInfo("Bank Branch", bankDetails.bankBranch)}
              {renderInfo("Account Type", bankDetails.accountType)}
              {renderInfo("Account Number", bankDetails.accountNumber)}
              {renderInfo("IFSC Code", bankDetails.ifscCode)}
              {renderInfo("Beneficiary Name", bankDetails.beneficiaryName)}
              {renderInfo(
                "Accounts Department POC: Name",
                bankDetails.accountsDepartmentPOCName
              )}
              {renderInfo(
                "Accounts Department POC: Number",
                bankDetails.accountsDepartmentPOCNumber
              )}
              {/* {renderInfo("Credit Remaining", bankDetails.creditRemaining)} */}
            </Grid>

            <Box mt={3}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  paddingBottom: "8px",
                  fontFamily: "Montserrat",
                }}
              >
                Uploaded Documents
              </Typography>
              {renderDocuments(bankDetails.documents || [], handlePreview)}
            </Box>
            <Box>
              <Typography
                mt={2}
                mb={2}
                variant="h6"
                sx={{
                  fontWeight: 600,
                  paddingBottom: "8px",
                  fontSize: isMobile ? "12px" : "inherit",
                  fontFamily: "Montserrat",
                }}
              >
                Customer Agreement
              </Typography>
              <Box display="flex" gap={2}>
                <Button
                  variant="outlined"
                  startIcon={<ArrowDownwardIcon sx={{ fontSize: 28 }} />}
                  sx={downloadAgreementButtonStyle}
                  onClick={handleDownloadAgreement}
                  disabled={!hasCustomerAgreementGenerated}
                >
                  Download Agreement
                </Button>
                <>
                  <input
                    type="file"
                    ref={signedAgreementInputRef}
                    style={{ display: "none" }}
                    onChange={handleSignedAgreementUpload}
                  />
                  <Button
                    variant="contained"
                    startIcon={<ArrowUpwardIcon sx={{ fontSize: 28 }} />}
                    sx={uploadSignedAgreementButtonStyle}
                    onClick={handleUploadSignedAgreementClick}
                    disabled={!hasCustomerAgreementGenerated}
                  >
                    Upload Signed Agreement
                  </Button>
                </>
                {renderDocuments(
                  profileData?.customerAgreements?.signed || [],
                  handlePreview
                )}
              </Box>
              <Box marginTop="16px">
                {fileError && (
                  <Typography color="error">{fileError}</Typography>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
      <FilePreviewDialog
        open={openDialog}
        downloadUrl={downloadUrl}
        handleClose={() => setOpenDialog(false)}
        previewFileUrl={previewFileUrl}
        previewFile={undefined}
        setDownloadUrl={setDownloadUrl}
        contentType={urlFileType}
      />
      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={error}
      />
    </>
  );
};

export default CustomerProfile;
